import type { MessageSortOrder, MessageSummary } from '@/api/types';

/**
 * The interleaving cursor (ADR 0056), as pure data and pure functions.
 *
 * A unified list is N per-account lists merged into one. Each account is a
 * **lane**: an opaque server cursor for its next page, the rows already fetched
 * from it but not yet emitted, whether it is exhausted, and whether it failed.
 * The whole of that is the cursor — a server cursor alone cannot express it,
 * because "I have read 50 rows from this mailbox and shown you 31 of them" is
 * client state.
 *
 * Nothing in this file imports React, TanStack Query or the API client: the
 * merge is the highest-risk logic in the feature and it is unit-testable with
 * no renderer and no network, exactly like `src/cache/messagePatches.ts`.
 *
 * `docs/HANDOFF.md` names cross-folder search as needing "an interleaving
 * cursor". This is that, and it does not care whether a lane is an account or
 * a folder.
 */

/**
 * One lane's position in the merge. `T` is the row: a message, or a
 * conversation in conversation view (ADR 0077) — the merge only compares.
 */
export interface UnifiedLane<T = MessageSummary> {
  /** The server's opaque cursor for this lane's next page; absent on page one. */
  cursor?: string;
  /** Fetched, not yet emitted. Always in `sort` order. */
  buffer: T[];
  /** The server said there is no next page. */
  exhausted: boolean;
  /** Set when this mailbox could not be read; it is then excluded, not retried. */
  failure?: UnifiedLaneFailure;
}

export interface UnifiedLaneFailure {
  /** What to say about this mailbox, already in the reader's words. */
  message: string;
  /** True when only signing in again can fix it, so "Try again" is not offered. */
  reauth: boolean;
}

/** The lanes, by account id. This object IS the `pageParam` of the merged list. */
export type UnifiedLanes<T = MessageSummary> = Readonly<Record<string, UnifiedLane<T>>>;

/** One entry of the "couldn't load" banner. */
export interface UnifiedLaneProblem extends UnifiedLaneFailure {
  accountId: string;
}

/**
 * The orders a merged list can offer.
 *
 * `arrival-asc` / `arrival-desc` are absent on purpose (ADR 0056 §2). Arrival
 * order is IMAP UID order, and UID 41 on one server has no relation to UID 41
 * on another: interleaving by it would produce something that is not an order.
 * Every order here compares a field that is on every row and means the same
 * thing everywhere.
 */
export const UNIFIED_SORTS: readonly MessageSortOrder[] = [
  'date-desc',
  'date-asc',
  'from-asc',
  'from-desc',
  'subject-asc',
  'subject-desc',
  'size-desc',
  'size-asc',
];

/** The `Date:` header, newest first — the same key the column shows (HANDOFF §5). */
export const DEFAULT_UNIFIED_SORT: MessageSortOrder = 'date-desc';

/**
 * What the sort control may offer: the intersection of what every mailbox
 * reported it can do, narrowed to the mergeable orders.
 *
 * Empty input means no lane has answered yet, and the answer is then the whole
 * mergeable set rather than nothing — an empty sort menu on a cold load would
 * be a worse lie than an optimistic one that the first page corrects.
 */
export function unifiedSortsFrom(
  available: readonly (readonly MessageSortOrder[])[],
): MessageSortOrder[] {
  const answered = available.filter((orders) => orders.length > 0);
  if (answered.length === 0) return [...UNIFIED_SORTS];
  return UNIFIED_SORTS.filter((order) => answered.every((orders) => orders.includes(order)));
}

/** Lanes for a fresh list: every account at page one, nothing buffered. */
export function emptyLanes<T = MessageSummary>(accountIds: readonly string[]): UnifiedLanes<T> {
  const lanes: Record<string, UnifiedLane<T>> = {};
  for (const accountId of accountIds) lanes[accountId] = { buffer: [], exhausted: false };
  return lanes;
}

/**
 * A total order over rows from different mailboxes.
 *
 * Total, not merely consistent: ties break on account id then message id, so
 * two messages with the same `Date:` always land in the same order however many
 * times the page is merged, re-merged or re-rendered. Without that, equal rows
 * could visibly swap places on a refetch.
 */
export function compareMessages(
  sort: MessageSortOrder,
): (a: MessageSummary, b: MessageSummary) => number {
  const primary = primaryComparator(sort);
  return (a, b) => {
    const byKey = primary(a, b);
    if (byKey !== 0) return byKey;
    if (a.accountId !== b.accountId) return a.accountId < b.accountId ? -1 : 1;
    if (a.id !== b.id) return a.id < b.id ? -1 : 1;
    return 0;
  };
}

function primaryComparator(
  sort: MessageSortOrder,
): (a: MessageSummary, b: MessageSummary) => number {
  switch (sort) {
    case 'date-asc':
      return (a, b) => dateOf(a) - dateOf(b);
    case 'from-asc':
      return (a, b) => senderOf(a).localeCompare(senderOf(b));
    case 'from-desc':
      return (a, b) => senderOf(b).localeCompare(senderOf(a));
    case 'subject-asc':
      return (a, b) => a.subject.localeCompare(b.subject);
    case 'subject-desc':
      return (a, b) => b.subject.localeCompare(a.subject);
    case 'size-asc':
      return (a, b) => a.sizeBytes - b.sizeBytes;
    case 'size-desc':
      return (a, b) => b.sizeBytes - a.sizeBytes;
    // Arrival order is not mergeable (see UNIFIED_SORTS); asking for it gets
    // the date order the lanes were actually fetched in rather than a throw,
    // because a stale remembered sort must not blank the list.
    default:
      return (a, b) => dateOf(b) - dateOf(a);
  }
}

function dateOf(message: MessageSummary): number {
  const value = Date.parse(message.date);
  return Number.isNaN(value) ? 0 : value;
}

function senderOf(message: MessageSummary): string {
  return (message.from.name ?? message.from.email).toLowerCase();
}

/**
 * The k-way merge, and the watermark that makes it stable.
 *
 * A row may be emitted only when no lane that still has pages could later
 * produce something belonging in front of it. That boundary is the *earliest*
 * "last buffered row" across every live, non-exhausted lane: past it, a lane's
 * next page is still unknown. Exhausted lanes can produce nothing more and
 * failed lanes will not be asked again, so neither takes part in the boundary —
 * without that exclusion one dead mailbox would freeze the list forever.
 *
 * A live lane with more pages and an empty buffer means the boundary is
 * unknowable: nothing is emitted and the lanes come back untouched, which is
 * the fetcher's signal to refill (see {@link lanesToRefill}).
 *
 * Returns the identical `lanes` reference when nothing moved.
 */
export function mergeLanes(
  lanes: UnifiedLanes,
  sort: MessageSortOrder,
): { items: MessageSummary[]; rest: UnifiedLanes } {
  return mergeLanesWith(lanes, compareMessages(sort));
}

/** The same merge over any row, given a total order for it. */
export function mergeLanesWith<T>(
  lanes: UnifiedLanes<T>,
  compare: (a: T, b: T) => number,
): { items: T[]; rest: UnifiedLanes<T> } {
  let boundary: T | undefined;
  for (const lane of Object.values(lanes)) {
    if (lane.failure || lane.exhausted) continue;
    const last = lane.buffer[lane.buffer.length - 1];
    if (!last) return { items: [], rest: lanes };
    if (boundary === undefined || compare(last, boundary) < 0) boundary = last;
  }

  const items: T[] = [];
  const rest: Record<string, UnifiedLane<T>> = {};
  let moved = false;

  for (const [accountId, lane] of Object.entries(lanes)) {
    if (lane.failure) {
      rest[accountId] = lane;
      continue;
    }
    let taken = 0;
    while (
      taken < lane.buffer.length &&
      (boundary === undefined || compare(lane.buffer[taken]!, boundary) <= 0)
    ) {
      items.push(lane.buffer[taken]!);
      taken += 1;
    }
    if (taken === 0) {
      rest[accountId] = lane;
      continue;
    }
    moved = true;
    rest[accountId] = { ...lane, buffer: lane.buffer.slice(taken) };
  }

  items.sort(compare);
  return { items, rest: moved ? rest : lanes };
}

/**
 * The lanes the next fetch must refill: live, not exhausted, nothing buffered.
 *
 * This is what keeps a long scroll to roughly one request per page instead of
 * N: a lane that is still holding rows is not asked again until the merge has
 * drained it.
 */
export function lanesToRefill<T>(lanes: UnifiedLanes<T>): string[] {
  return Object.entries(lanes)
    .filter(([, lane]) => !lane.failure && !lane.exhausted && lane.buffer.length === 0)
    .map(([accountId]) => accountId);
}

/** True while some live lane can still yield a row. A failed lane is not "more". */
export function lanesHaveMore<T>(lanes: UnifiedLanes<T>): boolean {
  return Object.values(lanes).some(
    (lane) => !lane.failure && (lane.buffer.length > 0 || !lane.exhausted),
  );
}

/** The mailboxes the banner has to name, in a stable order. */
export function laneFailures<T>(lanes: UnifiedLanes<T>): UnifiedLaneProblem[] {
  return Object.entries(lanes)
    .filter(([, lane]) => lane.failure !== undefined)
    .map(([accountId, lane]) => ({ accountId, ...lane.failure! }));
}
